import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/env';
import { extractAuthPayload, persistAuthTokens } from '../../utils/auth';
import { dashboardService } from '../../services/dashboardService';
import { cn } from '../../utils/cn';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (location.state) {
      const state = location.state as { message?: string; prefilledUsername?: string };
      if (state.prefilledUsername) {
        setUsername(state.prefilledUsername);
        setSuccessMessage(state.message || '');
        setTimeout(() => {
          if (passwordRef.current) {
            passwordRef.current.focus();
          }
        }, 100);
      }
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }
    
    try {
      const response = await axios.post(API_ENDPOINTS.LOGIN, {
        name: username,
        passcode: password
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data?.success === false) {
        const errorMessage = response.data.expandedError?.message || 'Login failed. Please try again.';
        
        if ((errorMessage.toLowerCase().includes('password') && errorMessage.toLowerCase().includes('incorrect')) ||
            errorMessage.toLowerCase().includes('no user')) {
          setError('Invalid credentials');
        } else {
          setError(errorMessage);
        }
        return;
      }
      
      const payload = extractAuthPayload(response.data);
      if (!payload || !persistAuthTokens(payload, username)) {
        setError('Login response is missing valid tokens. Please try again.');
        return;
      }

      dashboardService.invalidateUsersCache();
      navigate('/dashboard');
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.data?.expandedError?.message) {
          const errorMessage = err.response.data.expandedError.message;
          
          if ((errorMessage.toLowerCase().includes('password') && errorMessage.toLowerCase().includes('incorrect')) ||
              errorMessage.toLowerCase().includes('no user')) {
            setError('Invalid credentials');
          } else {
            setError(errorMessage);
          }
        } else if (err.response?.status === 401) {
          setError('Invalid username or password');
        } else if (err.response?.status === 400) {
          setError('Invalid request. Please check your credentials.');
        } else if (err.response?.status && err.response.status >= 500) {
          setError('Server error. Please try again later.');
        } else {
          setError('Login failed. Please try again.');
        }
      } else {
        setError('Network error. Please check your connection and try again.');
      }
    }
  };

  const inputClass = cn(
    'w-full rounded-xl border-0 bg-gray-100 px-4 py-3 text-gray-900',
    'placeholder:text-gray-400',
    'focus:outline-none focus:ring-2 focus:ring-blue-500/30'
  );

  return (
    <div
      className={cn(
        'min-h-screen homeBg',
        'bg-gradient-to-b from-slate-200 via-slate-400 to-zinc-900',
        'px-4 py-10 sm:px-6 sm:py-12 lg:px-8'
      )}
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-6 lg:flex-row lg:items-stretch lg:justify-center lg:gap-8">
        <section
          className={cn(
            'flex flex-1 flex-col rounded-3xl bg-white p-8 shadow-sm sm:p-10 lg:max-w-xl lg:p-12 blurLayerInteractive'
          )}
        >
          <p className="text-xs font-medium uppercase tracking-wider text-gray-400">
            Business payment workspace
          </p>
          <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight text-gray-900 sm:text-4xl">
            Make complex payment operations feel obvious from the first click.
          </h1>
          <p className="mt-4 text-base leading-relaxed text-gray-600">
            ZK Gate brings your payouts, balances, and team workflows into one place so you spend less time on tooling and more time moving money with confidence.
          </p>
          <div className="mt-8 rounded-2xl bg-gray-50 p-6 sm:p-8">
            <h2 className="text-sm font-semibold text-gray-900">
              What your team gets right away
            </h2>
            <ul className="mt-4 flex flex-col gap-3">
              {[
                'A single workspace to monitor transfers, settlements, and activity in real time.',
                'Clear roles and secure access so finance and ops stay aligned without friction.',
                'Faster onboarding with guided flows built for how your business actually pays out.',
              ].map((item) => (
                <li key={item} className="flex gap-3 text-sm text-gray-600">
                  <span
                    className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600"
                    aria-hidden
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section
          className={cn(
            'flex w-full flex-col rounded-3xl bg-white p-8 shadow-sm sm:p-10 lg:max-w-md lg:flex-none lg:p-12'
          )}
        >
          <header>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-400">
              Secure workspace
            </p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900">
              Welcome back
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Sign in to monitor balances, payouts, and team activity across your ZK Gate workspace.
            </p>
          </header>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-2">
            {error && (
              <div
                role="alert"
                className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800"
              >
                {error}
              </div>
            )}
            {successMessage && (
              <div
                role="status"
                className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
              >
                {successMessage}
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label htmlFor="username" className="text-sm font-semibold text-gray-700">
                Email or username
              </label>
              <input
                id="username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="password" className="text-sm font-semibold text-gray-700">
                Password
              </label>
              <div className="relative">
                <input
                  ref={passwordRef}
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={cn(inputClass, 'pr-16')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={cn(
                    'absolute right-3 top-1/2 -translate-y-1/2',
                    'text-sm font-medium text-blue-600',
                    'rounded-lg px-1 py-0.5',
                    'hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40'
                  )}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 focus:outline-none focus-visible:underline"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              className={cn(
                'mt-2 w-full rounded-xl bg-[#2297D1] py-3.5 text-base font-semibold text-white',
                'shadow-lg shadow-blue-600/35',
                'transition hover:bg-blue-700 hover:shadow-blue-600/45',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2'
              )}
            >
              Login
            </button>
          </form>

          <footer className="mt-8 flex flex-col items-center gap-4">
            <div className="h-px w-full bg-gray-200" />
            <Link
              to="/register"
              className="text-sm font-medium text-blue-500 hover:text-blue-600 focus:outline-none focus-visible:underline"
            >
              Register new account
            </Link>
          </footer>
        </section>
      </div>
    </div>
  );
};

export default Login;
